import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK, type DayOfWeek } from "@shared/types/foyer";
import type {
  Money,
  OfferAction,
  OfferActionKind,
  OfferAvailability,
  OfferFulfilment,
  OfferItem,
  RoomOffer,
  SlotPolicy,
} from "@shared/types/roomOffer";
import { roomOfferProblems } from "@shared/roomOffers";
import type { RoomOfferBody } from "../../http/foyer";
import { ItemFormSurface } from "../EditDrawer";
import LocalizedTextField from "../LocalizedTextField";
import AssetField from "../AssetField";
import EditorCard from "../template/EditorCard";
import LabelledControl from "../LabelledControl";
import { fieldGrid } from "../template/editorLayout";
import { tokens } from "../../theme";
import { useFormat } from "../../i18n/format";
import OfferItemsEditor from "./OfferItemsEditor";
import DemoPaymentsBanner from "./DemoPaymentsBanner";
import {
  offerKindHint,
  offerKindLabel,
  offerProblemMessage,
  shortDayLabel,
  type OfferField,
  type OfferProblem,
} from "./offerCopy";

/**
 * One pane, three kinds (`POS-5`).
 *
 * ## The rule the whole dialog exists to keep
 *
 * **A field a kind cannot use is not on screen.** A price box on a promotion is
 * how an operator ends up publishing "0 kr" to a television, and a slot
 * timetable on a room-service order is a form that teaches the operator the
 * model instead of the product. The kind picker at the top is therefore not a
 * label — it decides what the rest of the form IS.
 *
 * | kind | asks for |
 * |---|---|
 * | `promotion` | title, body, image, active window. Nothing else exists. |
 * | `order` | + price, fulfilment, and the goods a guest picks from |
 * | `booking · slots` | + optional price and a weekly timetable. No item list — its list is generated. |
 * | `booking · asap` | + the amenities a guest picks from, with no prices |
 *
 * ## Seeded once, remounted by key
 *
 * `useState(() => …)` and nothing that watches `initial`. The caller passes
 * `key={editing?.id ?? "new"}`, so opening a different record REMOUNTS this
 * component and the remount is the reset. A prop-watching effect cannot tell
 * "different record" from "same record reopened", and gets one of the two
 * wrong every time — silently, until somebody saves.
 * (`docs/memory/global/conventions/cms-list-and-editor.md`, rule 2.)
 *
 * ## `enabled` is not here
 *
 * Going live stays a control on the LIST, so it is never a side effect of
 * fixing a typo — the same corollary the same note draws about Publish.
 *
 * ## Validation is POS-2's, imported not re-implemented
 *
 * `roomOfferProblems()` is pure and shared precisely so the CMS can refuse
 * locally with the exact rules the server enforces. A second client-side
 * validator would diverge, and the divergence would show up as an offer the
 * CMS accepts and the server 422s — the worst of both.
 */

export interface OfferDraft {
  title: RoomOffer["title"];
  body: RoomOffer["body"];
  terms?: RoomOffer["terms"];
  imageUrl?: string;
  category?: string;
  /** A candidate for the home slot's "Featured offers (automatic)" (MEWS-7). */
  featured?: boolean;
  action: OfferAction;
  items: OfferItem[];
  availability: OfferAvailability;
}

/** The half of `SlotPolicy` that actually has hours in it. */
type Timetable = Extract<SlotPolicy, { mode: "slots" }>;

/**
 * A timetable that already works if the operator changes nothing.
 *
 * Six to nine in the evening, every day, half-hourly, two places, a fortnight
 * ahead — the spa evening from the demo. A blank timetable would be four
 * refusals before the first save, and the operator would learn the validator
 * rather than the product.
 */
export const DEFAULT_TIMETABLE: Timetable = {
  mode: "slots",
  days: [],
  startTime: "18:00",
  endTime: "21:00",
  intervalMinutes: 30,
  capacity: 2,
  horizonDays: 14,
};

/** `kr` — the demo property is Swedish and every price in it is major units. */
const DEFAULT_CURRENCY = "kr";

export function emptyOfferDraft(): OfferDraft {
  return {
    title: "",
    body: "",
    imageUrl: undefined,
    featured: false,
    action: { kind: "promotion" },
    items: [],
    availability: {},
  };
}

/** A saved row as the form edits it. A row IS its draft — nothing is dropped. */
export function draftOf(offer: RoomOffer): OfferDraft {
  return {
    title: offer.title,
    body: offer.body,
    terms: offer.terms,
    imageUrl: offer.imageUrl,
    category: offer.category,
    featured: offer.featured === true,
    action: offer.action,
    items: offer.items ?? [],
    availability: offer.availability ?? {},
  };
}

interface Props {
  open: boolean;
  /** The row being edited, or null for a new one. */
  initial: RoomOffer | null;
  supportedLocales: string[];
  defaultLocale: string;
  saving?: boolean;
  /** Codes the SERVER refused with, already turned into sentences. */
  serverProblems?: OfferProblem[];
  /** Anything that was not an `invalid_offer` refusal. */
  error?: string | null;
  onClose: () => void;
  /**
   * The PAGE saves. Resolve `true` on success and the surface slides out
   * (then calls `onClose`); `false` keeps it open to show the refusal.
   */
  onSave: (body: RoomOfferBody) => Promise<boolean>;
}

/**
 * New offer opens as a modal; editing an existing one slides in from the
 * right (`ItemFormSurface`). Same body, same footer.
 */
export default function OfferDialog({
  open,
  initial,
  supportedLocales,
  defaultLocale,
  saving,
  serverProblems,
  error,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation(["offers", "common"]);
  // Seeded ONCE. See the docblock: the caller remounts by key.
  const [draft, setDraft] = useState<OfferDraft>(() =>
    initial ? draftOf(initial) : emptyOfferDraft()
  );
  const [submitted, setSubmitted] = useState(false);
  /*
   * What the operator last configured for a kind they have since switched away
   * from. The model can only hold ONE action at a time, so switching
   * order → promotion → order would otherwise reset the fulfilment and the
   * timetable to their defaults every trip.
   */
  const [rememberedFulfil, setRememberedFulfil] = useState<OfferFulfilment>(
    initial?.action.kind === "order" ? initial.action.fulfil : "deliver"
  );
  /*
   * The timetable and the mode are remembered SEPARATELY, and that is not
   * bookkeeping for its own sake: "as soon as possible" is the absence of a
   * timetable, so storing it as the last slot policy would erase the hours the
   * operator typed the moment they looked at the other option.
   */
  const [rememberedTimetable, setRememberedTimetable] = useState<Timetable>(
    initial?.action.kind === "booking" && initial.action.slots.mode === "slots"
      ? initial.action.slots
      : DEFAULT_TIMETABLE
  );
  const [rememberedMode, setRememberedMode] = useState<SlotPolicy["mode"]>(
    initial?.action.kind === "booking" ? initial.action.slots.mode : "slots"
  );

  /*
   * The currency is dialog state rather than a field of the price.
   *
   * `Money` carries its own currency, but a price does not EXIST until an
   * amount is typed — and an operator who sets the currency first would
   * otherwise have to be given `{ amount: 0 }` to hang it on, which is exactly
   * the zero the revenue series must never see. Held here, it survives an empty
   * amount and is what every item row is priced in too.
   */
  const [currency, setCurrency] = useState(
    () => initialPrice(initial)?.currency || DEFAULT_CURRENCY
  );

  const kind = draft.action.kind;
  const price = "price" in draft.action ? draft.action.price : undefined;
  const slots = draft.action.kind === "booking" ? draft.action.slots : null;
  // Narrowed once, here: TypeScript drops a property narrowing the moment it
  // crosses into a callback, so the handlers below need the union already
  // resolved rather than a `kind ===` check they cannot see through.
  const orderAction = draft.action.kind === "order" ? draft.action : null;
  /** The item list exists for goods and for amenities, and nowhere else. */
  /**
   * Does this offer have a pick list at all?
   *
   * The offer-level price only means anything when there is nothing to pick -
   * "Late checkout, 395 kr" is one thing at one price. The moment a menu exists
   * the guest pays for what they choose, and an item price ADDS to the line
   * (docs/room-offers.md §2), so a 100 kr offer carrying a 120 kr item charges
   * 220. Defensible arithmetic, indefensible form: you type a price at the top,
   * type prices again underneath, and nothing says the first was a surcharge.
   *
   * So the box is disabled as soon as there is a list - shown, so you can see
   * WHY it is not yours to fill, rather than vanishing and leaving you to wonder
   * where the price went.
   */
  const hasItems = draft.items.length > 0;

  /**
   * Disabling the box is not enough: a price typed BEFORE the list existed would
   * keep adding from behind a field nobody can edit, which is worse than the
   * confusion it was meant to fix. So it is cleared for real.
   */
  useEffect(() => {
    if (!hasItems) return;
    if (draft.action.kind === "promotion") return;
    if (!draft.action.price) return;
    setAction({ ...draft.action, price: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasItems, draft.action]);

  const showItems =
    kind === "order" || (kind === "booking" && slots?.mode === "asap");

  const localProblems = useMemo(
    () =>
      roomOfferProblems(
        {
          title: draft.title,
          action: draft.action,
          availability: draft.availability,
          items: draft.items,
        },
        { creating: !initial }
      ).map((code) => offerProblemMessage(t, code)),
    [draft, initial, t]
  );

  // Local rules only bite once Save has been pressed - a form that goes red
  // while somebody is still typing the title is a form that shouts first and
  // reads second. The server's own refusal is shown the moment it arrives.
  const shown = [
    ...(submitted ? localProblems : []),
    ...(serverProblems ?? []),
  ];
  const forField = (field: OfferField) =>
    shown
      .filter((p) => p.field === field)
      .map((p) => p.message)
      .join(" ");

  const setAction = (action: OfferAction) =>
    setDraft((d) => ({ ...d, action }));

  /** An amount, or `undefined` for free. The currency comes from state above. */
  const setAmount = (amount: number | undefined) => {
    if (draft.action.kind === "promotion") return;
    setAction({
      ...draft.action,
      price: amount === undefined ? undefined : { amount, currency },
    });
  };

  /**
   * Re-stamp the currency onto every price already typed.
   *
   * The items too, not only the offer's own. `Money` carries its currency per
   * price, so a menu whose lines were added before the operator changed "kr" to
   * "EUR" would keep quoting kronor beside euros on the same screen — and
   * nothing in the model would call that wrong.
   */
  const changeCurrency = (next: string) => {
    setCurrency(next);
    setDraft((d) => ({
      ...d,
      action:
        d.action.kind === "promotion" || !d.action.price
          ? d.action
          : { ...d.action, price: { ...d.action.price, currency: next } },
      items: d.items.map((it) =>
        it.price ? { ...it, price: { ...it.price, currency: next } } : it
      ),
    }));
  };

  const setSlots = (next: SlotPolicy) => {
    setRememberedMode(next.mode);
    if (next.mode === "slots") setRememberedTimetable(next);
    if (draft.action.kind !== "booking") return;
    setAction({ ...draft.action, slots: next });
  };

  /**
   * Change what pressing the offer does.
   *
   * Switching to a promotion CLEARS the price and the pick list rather than
   * carrying them along invisibly: the server refuses a promotion that has
   * either (`promotion_takes_no_price` / `promotion_takes_no_items`), and a
   * dialog that let the operator save into that refusal would be teaching them
   * the validator. The banner below says it out loud before they press Save.
   */
  const changeKind = (next: OfferActionKind) => {
    if (next === kind) return;
    if (next === "promotion") {
      setDraft((d) => ({ ...d, action: { kind: "promotion" }, items: [] }));
      return;
    }
    if (next === "order") {
      setAction({ kind: "order", price, fulfil: rememberedFulfil });
      return;
    }
    setAction({
      kind: "booking",
      price,
      slots: rememberedMode === "asap" ? { mode: "asap" } : rememberedTimetable,
    });
  };

  const submit = async (dismiss: () => void) => {
    setSubmitted(true);
    if (localProblems.length > 0) return;
    const ok = await onSave({
      title: draft.title,
      body: draft.body,
      // Every optional field is STATED, even when empty: this is a PATCH, and
      // an omitted key leaves the stored value alone. Clearing the small print
      // has to be a thing the operator can actually do.
      terms: draft.terms ?? "",
      imageUrl: draft.imageUrl ?? "",
      category: draft.category ?? "",
      // Stated every time, like the fields above: unticking must reach the row.
      featured: draft.featured === true,
      action: draft.action,
      /*
       * What is on screen is what is saved.
       *
       * A `booking · slots` hides the item list because its list is GENERATED,
       * and a promotion has none at all. Sending the rows anyway would strand
       * them: unreachable in the editor forever, still filtered into the TV
       * feed by `resolveForTv`, and invisible to the operator who put them
       * there. `order` numbers are re-stamped from the index so the array and
       * the field cannot disagree.
       */
      items: showItems
        ? draft.items.map((it, order) => ({ ...it, order }))
        : [],
      availability: draft.availability,
    });
    if (ok) dismiss();
  };

  const loc = { supportedLocales, defaultLocale };
  /**
   * Warn before a save throws away rows the current kind cannot show.
   *
   * Read off the SAVED row, not the draft: by the time a promotion is selected
   * the draft has already been emptied, so the draft can no longer say what is
   * about to be lost.
   */
  const willDropSaved =
    !showItems &&
    Boolean(initial) &&
    ((initial?.items?.length ?? 0) > 0 ||
      (kind === "promotion" && initialPrice(initial) !== undefined));

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      open={open}
      onClose={onClose}
      maxWidth="md"
      drawerWidth={900}
      busy={saving}
      data-testid="offer-dialog"
      title={initial ? t("offerDialog.editTitle") : t("offerDialog.newTitle")}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit(dismiss)}
            disabled={saving}
            data-testid="offer-save"
          >
            {saving ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2}>
        {/* POS-8, §6: the editor is where a price gets typed, so it is one of
              the surfaces that must say the money is imaginary. */}
        <DemoPaymentsBanner sx={{ mb: 0 }} />
        {error && <Alert severity="error">{error}</Alert>}
        {willDropSaved && (
          <Alert severity="warning" data-testid="offer-drop-warning">
            {kind === "promotion"
              ? t("offerDialog.dropWarningPromotion")
              : t("offerDialog.dropWarningBooking")}
          </Alert>
        )}

        <EditorCard
          eyebrow={t("offerDialog.kindEyebrow")}
          data-testid="offer-kind"
        >
          <Box sx={fieldGrid(240)}>
            {(["promotion", "order", "booking"] as const).map((k) => (
              <KindCard
                key={k}
                kind={k}
                selected={k === kind}
                onSelect={() => changeKind(k)}
              />
            ))}
          </Box>
          {forField("action") && (
            <Typography variant="meta" sx={{ mt: 1.5, color: tokens.error }}>
              {forField("action")}
            </Typography>
          )}
        </EditorCard>

        <EditorCard
          eyebrow={t("offerDialog.guestSeesEyebrow")}
          data-testid="offer-copy"
        >
          <Stack spacing={2}>
            <Box>
              <LocalizedTextField
                label={t("common:labels.title")}
                value={draft.title}
                {...loc}
                onChange={(title) => setDraft((d) => ({ ...d, title }))}
                placeholder={t("offerDialog.titlePlaceholder")}
                testId="offer-title"
              />
              {forField("title") && (
                <Typography
                  variant="meta"
                  role="alert"
                  sx={{ mt: 0.75, color: tokens.error }}
                >
                  {forField("title")}
                </Typography>
              )}
            </Box>
            <LocalizedTextField
              label={t("common:labels.description")}
              value={draft.body}
              multiline
              rows={3}
              {...loc}
              onChange={(body) => setDraft((d) => ({ ...d, body }))}
              testId="offer-body"
            />
            <Box sx={fieldGrid(280)}>
              <Box>
                <AssetField
                  label={t("common:labels.picture")}
                  value={draft.imageUrl ?? ""}
                  preview
                  onChange={(v) =>
                    setDraft((d) => ({ ...d, imageUrl: v || undefined }))
                  }
                  testId="offer-image"
                />
              </Box>
              <TextField
                size="small"
                label={t("offerDialog.group")}
                placeholder="spa"
                value={draft.category ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    category: e.target.value || undefined,
                  }))
                }
                inputProps={{ "data-testid": "offer-category" }}
              />
            </Box>
            <LocalizedTextField
              label={t("offerDialog.smallPrint")}
              value={draft.terms}
              multiline
              rows={2}
              {...loc}
              onChange={(terms) => setDraft((d) => ({ ...d, terms }))}
              testId="offer-terms"
            />
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.featured === true}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, featured: e.target.checked }))
                    }
                    inputProps={{ "data-testid": "offer-featured" } as object}
                  />
                }
                label={t("offerDialog.featured")}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {t("offerDialog.featuredHelp")}
              </Typography>
            </Box>
          </Stack>
        </EditorCard>

        {orderAction && (
          <EditorCard
            eyebrow={t("offerDialog.orderEyebrow")}
            data-testid="offer-order"
          >
            <Box sx={fieldGrid(240)}>
              {hasItems ? (
                // Disabled rather than hidden, and the currency stays live:
                // the menu below is priced in it.
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label={t("common:labels.price")}
                    value=""
                    disabled
                    // Float the label, or MUI parks it inside the box and the
                    // placeholder never shows - a disabled field with no
                    // explanation just looks broken.
                    InputLabelProps={{ shrink: true }}
                    placeholder={t("offerDialog.setByList")}
                    inputProps={{ "data-testid": "offer-price-disabled" }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label={t("common:labels.currency")}
                    value={currency}
                    onChange={(e) => changeCurrency(e.target.value)}
                    inputProps={{
                      maxLength: 8,
                      "data-testid": "offer-price-currency",
                    }}
                    sx={{ width: 96, flex: "none" }}
                  />
                </Stack>
              ) : (
                <PriceField
                  price={price}
                  currency={currency}
                  onAmount={setAmount}
                  onCurrency={changeCurrency}
                  error={forField("price")}
                  testId="offer-price"
                />
              )}
              <LabelledControl label={t("offerDialog.fulfilment")}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={orderAction.fulfil}
                  onChange={(_, v: OfferFulfilment | null) => {
                    if (!v) return;
                    setRememberedFulfil(v);
                    setAction({ ...orderAction, fulfil: v });
                  }}
                  data-testid="offer-fulfil"
                >
                  <ToggleButton value="deliver">
                    {t("offerDialog.deliver")}
                  </ToggleButton>
                  <ToggleButton value="desk">
                    {t("offerDialog.desk")}
                  </ToggleButton>
                </ToggleButtonGroup>
                {forField("fulfil") && (
                  <Typography variant="meta" sx={{ color: tokens.error }}>
                    {forField("fulfil")}
                  </Typography>
                )}
              </LabelledControl>
            </Box>
          </EditorCard>
        )}

        {kind === "booking" && slots && (
          <EditorCard
            eyebrow={t("offerDialog.bookingEyebrow")}
            data-testid="offer-booking"
          >
            <Stack spacing={2}>
              <Box sx={fieldGrid(240)}>
                <PriceField
                  price={price}
                  currency={currency}
                  onAmount={setAmount}
                  onCurrency={changeCurrency}
                  error={forField("price")}
                  testId="offer-price"
                />
                <LabelledControl label={t("offerDialog.howGuestBooks")}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={slots.mode}
                    onChange={(_, v: "slots" | "asap" | null) => {
                      if (!v || v === slots.mode) return;
                      setSlots(
                        v === "asap" ? { mode: "asap" } : rememberedTimetable
                      );
                    }}
                    data-testid="offer-slot-mode"
                  >
                    <ToggleButton value="slots">
                      {t("offerDialog.pickTime")}
                    </ToggleButton>
                    <ToggleButton value="asap">
                      {t("offerDialog.asap")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </LabelledControl>
              </Box>

              {slots.mode === "asap" ? (
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("offerDialog.asapHelp")}
                </Typography>
              ) : (
                <SlotPolicyFields
                  policy={slots}
                  onChange={setSlots}
                  error={forField("slots")}
                />
              )}
            </Stack>
          </EditorCard>
        )}

        {showItems && (
          <EditorCard
            eyebrow={
              kind === "order"
                ? t("offerDialog.itemsOrderEyebrow")
                : t("offerDialog.itemsAmenitiesEyebrow")
            }
            hint={
              kind === "order"
                ? t("offerDialog.itemsOrderHint")
                : t("offerDialog.itemsAmenitiesHint")
            }
            data-testid="offer-items"
          >
            <OfferItemsEditor
              items={draft.items}
              onChange={(items) => setDraft((d) => ({ ...d, items }))}
              priced={kind === "order"}
              currency={currency}
              {...loc}
              error={forField("items")}
            />
          </EditorCard>
        )}

        <EditorCard
          eyebrow={t("offerDialog.availabilityEyebrow")}
          data-testid="offer-availability"
        >
          <AvailabilityFields
            value={draft.availability}
            onChange={(availability) =>
              setDraft((d) => ({ ...d, availability }))
            }
            error={forField("availability")}
          />
        </EditorCard>
      </Stack>
    </ItemFormSurface>
  );
}

function KindCard({
  kind,
  selected,
  onSelect,
}: {
  kind: OfferActionKind;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("offers");
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      aria-label={offerKindLabel(t, kind)}
      onClick={onSelect}
      data-testid={`offer-kind-${kind}`}
      sx={{
        display: "block",
        textAlign: "left",
        p: 1.75,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: selected ? tokens.brand : tokens.divider,
        bgcolor: selected ? tokens.brandTintSoft : tokens.surface,
        height: "100%",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
      >
        {offerKindLabel(t, kind)}
      </Typography>
      <Typography variant="meta" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
        {offerKindHint(t, kind)}
      </Typography>
    </ButtonBase>
  );
}

function PriceField({
  price,
  currency,
  onAmount,
  onCurrency,
  error,
  testId,
}: {
  price: Money | undefined;
  currency: string;
  onAmount: (next: number | undefined) => void;
  onCurrency: (next: string) => void;
  error?: string;
  testId: string;
}) {
  const { t } = useTranslation("common");
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        size="small"
        label={t("labels.price")}
        type="number"
        value={price?.amount ?? ""}
        onChange={(e) => {
          const text = e.target.value.trim();
          if (text === "") return onAmount(undefined);
          const amount = Number(text);
          onAmount(Number.isFinite(amount) ? amount : Number.NaN);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{currency}</InputAdornment>
          ),
        }}
        inputProps={{ min: 0, "data-testid": testId }}
        // Absent is FREE, and that is not the same as zero: a zero would put a
        // 0 kr line into the revenue series (docs/room-offers.md §2).
        helperText={error}
        error={Boolean(error)}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        label={t("labels.currency")}
        value={currency}
        onChange={(e) => onCurrency(e.target.value)}
        inputProps={{ maxLength: 8, "data-testid": `${testId}-currency` }}
        sx={{ width: 96, flex: "none" }}
      />
    </Stack>
  );
}

/** The saved row's price, whichever kind carried it. */
function initialPrice(offer: RoomOffer | null): Money | undefined {
  if (!offer) return undefined;
  return "price" in offer.action ? offer.action.price : undefined;
}

/**
 * A 24-hour time field that is 24-hour everywhere.
 *
 * `<input type="time">` renders AM/PM or 24h according to the BROWSER's locale,
 * not the page's and not its `lang` attribute - so the same form reads 07:00 PM
 * on an en-US Mac and 19:00 on a Swedish one, while the value it stores is
 * "HH:mm" either way. Two clocks in one dialog is how 19:00 gets typed as 7:00
 * and lands in the morning, and the hotel finds out from a guest.
 *
 * Two selects instead. No dependency - this repo has no date-picker library and
 * adding one from a Mac rewrites the lockfile
 * (docs/memory/global/pitfalls/npm-lockfile-platform-pruning.md) - no locale,
 * and nothing to mistype. Minutes step by 5, finer than any hotel schedules.
 */
function Time24Field({
  label,
  value,
  onChange,
  helperText,
  allowEmpty,
  testId,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  helperText?: string;
  allowEmpty?: boolean;
  testId: string;
}) {
  const [hh, mm] = (value ?? "").split(":");
  const setPart = (nextHh: string, nextMm: string) => {
    if (allowEmpty && nextHh === "" && nextMm === "")
      return onChange(undefined);
    onChange(`${nextHh || "00"}:${nextMm || "00"}`);
  };
  const hours = Array.from({ length: 24 }, (_, h) =>
    String(h).padStart(2, "0")
  );
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0")
  );
  // Both halves are TextFields carrying the SAME outlined chrome as every other
  // field in this grid, and the label floats on the border rather than sitting
  // above the box. A hand-rolled label stacked on top makes this control taller
  // than its neighbours, so "Every 60 min" and "Places per time" ride up and the
  // row stops reading as a row.
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      <TextField
        select
        size="small"
        label={label}
        value={hh ?? ""}
        onChange={(e) => setPart(e.target.value, mm ?? "00")}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ native: true }}
        inputProps={{ "data-testid": `${testId}-hh` }}
        helperText={helperText}
        sx={{ minWidth: 84 }}
      >
        {allowEmpty ? <option value="">--</option> : null}
        {hours.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </TextField>
      <Typography sx={{ color: tokens.inkMuted, mt: 1.1 }}>:</Typography>
      <TextField
        select
        size="small"
        label="&nbsp;"
        value={mm ?? ""}
        onChange={(e) => setPart(hh ?? "00", e.target.value)}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ native: true }}
        inputProps={{ "data-testid": `${testId}-mm` }}
        sx={{ minWidth: 84 }}
      >
        {allowEmpty ? <option value="">--</option> : null}
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </TextField>
    </Stack>
  );
}

function DayPicker({
  value,
  onChange,
  label,
  testId,
}: {
  value: DayOfWeek[];
  onChange: (next: DayOfWeek[]) => void;
  label: string;
  testId: string;
}) {
  const fmt = useFormat();
  return (
    <LabelledControl label={label}>
      <ToggleButtonGroup
        size="small"
        value={value}
        onChange={(_, next: DayOfWeek[]) => onChange(next)}
        data-testid={testId}
      >
        {DAYS_OF_WEEK.map((d) => (
          <ToggleButton
            key={d}
            value={d}
            sx={{
              px: 1.1,
              fontWeight: "fontWeightSemibold",
              // An unselected MUI toggle differs from a selected one by a few
              // percent of grey, which on a form this dense is not a state -
              // it is a guess. Selected is solid blue with white text, so "on"
              // is legible across the room and across the whole row at once.
              "&.Mui-selected": {
                backgroundColor: tokens.brand,
                color: "#fff",
                borderColor: tokens.brand,
                "&:hover": { backgroundColor: tokens.brandStrong },
              },
            }}
          >
            {shortDayLabel(fmt, d)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </LabelledControl>
  );
}

/** A whole number, or `fallback` when the box is empty or nonsense. */
function intOr(raw: string, fallback: number): number {
  const text = raw.trim();
  if (text === "") return fallback;
  const n = Number(text);
  return Number.isFinite(n) ? Math.round(n) : fallback;
}

function SlotPolicyFields({
  policy,
  onChange,
  error,
}: {
  policy: Timetable;
  onChange: (next: SlotPolicy) => void;
  error?: string;
}) {
  const { t } = useTranslation("offers");
  const set = (part: Partial<Timetable>) => onChange({ ...policy, ...part });
  return (
    <Stack spacing={2}>
      <DayPicker
        value={policy.days}
        onChange={(days) => set({ days })}
        label={t("offerDialog.slotDays")}
        testId="offer-slot-days"
      />
      <Box sx={fieldGrid(180)}>
        <Time24Field
          label={t("offerDialog.firstTime")}
          value={policy.startTime}
          onChange={(v) => set({ startTime: v ?? "00:00" })}
          testId="offer-slot-start"
        />
        <Time24Field
          label={t("offerDialog.lastTime")}
          value={policy.endTime}
          onChange={(v) => set({ endTime: v ?? "00:00" })}
          testId="offer-slot-end"
        />
        <TextField
          size="small"
          label={t("offerDialog.every")}
          type="number"
          value={policy.intervalMinutes}
          onChange={(e) => set({ intervalMinutes: intOr(e.target.value, 30) })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {t("offerDialog.minutesUnit")}
              </InputAdornment>
            ),
          }}
          inputProps={{
            min: 5,
            max: 720,
            "data-testid": "offer-slot-interval",
          }}
        />
        <TextField
          size="small"
          label={t("offerDialog.placesPerTime")}
          type="number"
          value={policy.capacity}
          onChange={(e) => set({ capacity: intOr(e.target.value, 1) })}
          inputProps={{
            min: 1,
            max: 999,
            "data-testid": "offer-slot-capacity",
          }}
        />
        <TextField
          size="small"
          label={t("offerDialog.bookableAhead")}
          type="number"
          value={policy.horizonDays}
          onChange={(e) => set({ horizonDays: intOr(e.target.value, 14) })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {t("offerDialog.daysUnit")}
              </InputAdornment>
            ),
          }}
          inputProps={{ min: 1, max: 30, "data-testid": "offer-slot-horizon" }}
        />
        <TextField
          size="small"
          label={t("offerDialog.noticeNeeded")}
          type="number"
          value={policy.minNoticeMinutes ?? ""}
          onChange={(e) => {
            const text = e.target.value.trim();
            set({
              minNoticeMinutes: text === "" ? undefined : intOr(text, 0),
            });
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {t("offerDialog.minutesUnit")}
              </InputAdornment>
            ),
          }}
          inputProps={{ min: 0, "data-testid": "offer-slot-notice" }}
        />
      </Box>
      {error && (
        <Typography variant="meta" sx={{ color: tokens.error }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
}

function AvailabilityFields({
  value,
  onChange,
  error,
}: {
  value: OfferAvailability;
  onChange: (next: OfferAvailability) => void;
  error?: string;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const set = (part: Partial<OfferAvailability>) =>
    onChange({ ...value, ...part });
  const text = (v: string) => (v.trim() === "" ? undefined : v);
  return (
    <Stack spacing={2}>
      <Box sx={fieldGrid(180)}>
        <TextField
          size="small"
          label={t("common:labels.from")}
          type="date"
          value={value.startDate ?? ""}
          onChange={(e) => set({ startDate: text(e.target.value) })}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            "data-testid": "offer-window-start-date",
          }}
        />
        <TextField
          size="small"
          label={t("offerDialog.until")}
          type="date"
          value={value.endDate ?? ""}
          onChange={(e) => set({ endDate: text(e.target.value) })}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "offer-window-end-date" }}
        />
        <Time24Field
          label={t("offerDialog.showsFrom")}
          value={value.startTime}
          onChange={(v) => set({ startTime: v })}
          allowEmpty
          testId="offer-window-start-time"
        />
        <Time24Field
          label={t("offerDialog.showsUntil")}
          value={value.endTime}
          onChange={(v) => set({ endTime: v })}
          allowEmpty
          testId="offer-window-end-time"
        />
      </Box>
      <DayPicker
        value={value.days ?? []}
        onChange={(days) => set({ days: days.length > 0 ? days : undefined })}
        label={t("offerDialog.daysItShows")}
        testId="offer-window-days"
      />
      {error && (
        <Typography variant="meta" sx={{ color: tokens.error }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
}
